import type { NewsUpdate } from './types'
import type { BadgePack, PointsAwardEntry, BadgeAwardEntry, GamificationSettings } from '@/lib/gamification/types'
// Relative, not '@/': the api's PGlite ladder cases require this module
// through ts-node, which does not know the web path alias.
import { sortLadder } from '../gamification/level-ladder'
import { calendarDayOf } from '../time/zoned-day'
import { browserTimeZone } from '../time/zone'

// These transforms hand the portal RAW values (ISO instants, calendar days,
// names and codes); <PortalPreview> words them in the reader's language with
// components/user-portal/learner-i18n.tsx (locale audit 2026-09-26, L-13).
// They used to return English ("News Updates", "5 min ago", "Phishing 101
// Certificate", "Pending", "Sep 30, 2026"), which an Arabic or Hindi learner
// then read inside an otherwise translated page.

/**
 * The category of a news article, a code rather than a label: the portal
 * names it in the reader's language. Policy items carry the admin's own words.
 */
export const NEWS_CATEGORY = 'news'

// ---------------------------------------------------------------------------
// Shared prop shapes used by PortalPreview
// ---------------------------------------------------------------------------

export interface NewsItemProp {
  /** NEWS_CATEGORY for a news article. */
  category: string
  title: string
  description: string
  image: string
  content?: string
  /** When it was published, as the API sent it (ISO). */
  publishedAt?: string
}

export interface TrainingSlideProp {
  title: string
  status: string
  /**
   * The due date as the workspace's calendar day ('2026-09-30'), '' when there
   * is none; the portal names it in the reader's language. (An English
   * "Sep 30, 2026" `dueDate` used to ride along; nothing read it.) Optional so
   * hand-written preview arrays need not carry it.
   */
  dueDay?: string
  /**
   * The zone `dueDay` is a day in (the enrolment's workspace), so "due soon"
   * counts that zone's days, not the viewer's. Absent when none was known.
   */
  dueTimeZone?: string
  image: string
  /**
   * Real progress percent (0-100) derived from enrollment.progress.
   * Single source of truth for the carousel card body + progress bar — eliminates
   * the bug class where badge=Completed but body="Not started" + 0% bar (ticket 696c6b46).
   * Optional so older consumers with hardcoded slide arrays don't TSC-fail.
   */
  progress?: number
  /**
   * FIX #682: enrollment primary key — enables the "Start Training →" button in
   * Important Updates to navigate to /portal/training/[enrollmentId].
   * Optional so hardcoded preview slide arrays in theme-builder don't TSC-fail.
   */
  enrollmentId?: string
}

export interface CourseCardProp {
  title: string
  image: string
  attempts: string
  status: string
  statusColor: string
  /**
   * The due date as the workspace's calendar day ('2026-12-12'), '' when the
   * enrollment has no deadline. The portal names it in the reader's language,
   * and its "Next deadline" and "due soon" count days to it. Optional so theme
   * previews with hardcoded sample arrays need not carry it.
   */
  dueDay?: string
  /**
   * The zone `dueDay` is a day in (the enrolment's workspace): "Next deadline"
   * counts days to it in that zone. Absent when none was known.
   */
  dueTimeZone?: string
  /**
   * Real progress percent (0-100) derived from enrollment.progress.
   * Used by the trainings-tab card to branch the CTA copy
   * ("Start" / "Continue" / "Review") and show real progress instead of a
   * hardcoded "Start Training" for every status (ticket 9b36b6ae).
   */
  progress?: number
  /**
   * FIX #681: enrollment primary key — enables the "Start Training" button in
   * the Trainings tab to navigate to /portal/training/[enrollmentId].
   * Optional so hardcoded preview card arrays in theme-builder don't TSC-fail.
   */
  enrollmentId?: string
}

export interface PointsHistoryProp {
  activity: string
  points: string
  /**
   * When the points were awarded: the instant the API sent (ISO). The portal
   * words it ("5 min ago" / "قبل 5 دقائق") in the reader's language.
   */
  date: string
}

export interface BadgeCardProp {
  name: string
  icon: string
  earned: boolean
  description?: string
  earnedAt?: string
  relatedTraining?: string
  pointsEarned?: number
  eventCount?: number
}

export interface CertificateCardProp {
  /** The course's name; the portal titles the card "{course} Certificate" in the reader's language. */
  name: string
  /** When it was issued (ISO), or null while it has not been ("Pending" on screen). */
  date: string | null
  status: string
}

export interface LevelDataProp {
  id: number
  name: string
  pointsRequired: number
  badge: string
}

// ---------------------------------------------------------------------------
// Enrollment / Course types (lightweight, avoids importing zod schemas)
// ---------------------------------------------------------------------------

interface EnrollmentLike {
  /** Enrollment row primary key — used to navigate to /portal/training/[enrollmentId] */
  id?: string
  learnerEmail: string
  courseId: string
  courseName: string
  status: string
  progress: number
  dueDate: string
  /**
   * The zone this enrolment's due date is a day in (its workspace's). Wins over
   * the zone handed to a transform, so a learner enrolled from two workspaces
   * sees each due date as its own workspace's day.
   */
  dueTimeZone?: string
  completionDate: string | null
  /**
   * The course's picture, as GET /v1/portal/enrollments sends it
   * (`courseThumbnailUrl`). The portal page has no course list to hand the
   * transforms, so this is where a learner's card gets its picture; '' or
   * absent means the course has none (spec 2026-10-07 global deck catalog,
   * §WP5 / Q7).
   */
  courseThumbnailUrl?: string
}

interface CourseLike {
  id: string
  title: string
  thumbnail: string
  certificateEnabled?: boolean
}

// ---------------------------------------------------------------------------
// Transforms
// ---------------------------------------------------------------------------

export function transformNewsToProps(newsUpdates: NewsUpdate[]): NewsItemProp[] {
  const now = Date.now()
  return newsUpdates
    .filter((n) => {
      // FIX: D-D BUG#1 (ticket b0249912) — Important Updates carousel rendered 0 slides.
      // Root cause: filter used `!n.isActive` but the BE response carries `status` (Title
      // Case enum) not `isActive` (deprecated bool). All items had isActive=undefined,
      // making every item falsy and excluding all of them from the carousel.
      // Fix: prefer canonical `status === 'Published'` check; fall back to deprecated
      // `isActive` only for payloads that pre-date the status field.
      const isPublished = n.status
        ? n.status === 'Published'
        : Boolean(n.isActive)
      if (!isPublished) return false
      const start = new Date(n.publishStart).getTime()
      const end = new Date(n.publishEnd).getTime()
      // Show if current time is within the publish window (or if dates are invalid/missing)
      if (isNaN(start) || isNaN(end)) return true
      return now >= start && now <= end
    })
    .map((n) => ({
      category: NEWS_CATEGORY,
      title: n.title,
      description: n.content,
      image:
        n.thumbnailUrl ||
        '',
      content: n.content,
      publishedAt: n.createdOn || n.publishStart,
    }))
}

// FIX (2026-05-14, ticket 9b36b6ae + 696c6b46): BE returns enrollment.status
// in Title Case ('Completed' | 'In Progress' | 'Not Started' | 'Overdue' —
// see lib/lms/schemas.ts:enrollmentStatusSchema). Previous keys were
// UPPER_SNAKE_CASE so EVERY enrollment fell through to the default branch:
// statusColor was always NOT_STARTED grey, label was passed through raw.
// Keys now match the canonical Title Case enum.
const STATUS_COLOR_MAP: Record<string, string> = {
  'In Progress':
    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  Completed:
    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  'Not Started': 'bg-muted/50 text-muted-foreground',
  Overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
}

/**
 * A due date as the WORKSPACE's calendar day ('2026-09-30'), '' when there is
 * none (2026-09-26): the API stores the end of that day in the workspace zone,
 * so the viewer's own zone showed the next day to anyone east of the workspace.
 * Without a zone, the viewer's is read the same way, which still keeps older
 * rows (a #53 23:59:59.999Z, a UTC midnight) on the day they were set for.
 */
function dueDayOf(dateStr: string, timeZone?: string): string {
  return calendarDayOf(dateStr, timeZone ?? browserTimeZone()) ?? ''
}

export function transformToTrainingSlides(
  enrollments: EnrollmentLike[],
  courses: CourseLike[],
  employeeEmail?: string,
  /** The workspace zone; due dates print as that zone's day. */
  timeZone?: string,
): TrainingSlideProp[] {
  const courseMap = new Map(courses.map((c) => [c.id, c]))
  return enrollments
    .filter(
      (e) =>
        // FIX (2026-05-14, ticket 696c6b46): status comparison was
        // UPPER_SNAKE_CASE ('COMPLETED') against a Title Case BE enum
        // ('Completed'). Result: completed enrollments NEVER matched
        // the filter and leaked into the carousel — producing the
        // contradictory "badge=Completed + body=Not started + 0%" cards.
        (!employeeEmail || e.learnerEmail === employeeEmail) &&
        e.status !== 'Completed',
    )
    .slice(0, 5)
    .map((e) => {
      const course = courseMap.get(e.courseId)
      const zone = e.dueTimeZone ?? timeZone
      const dueDay = dueDayOf(e.dueDate, zone)
      return {
        title: e.courseName,
        status: e.status,
        dueDay,
        ...(zone ? { dueTimeZone: zone } : {}),
        // The enrolment's own course picture first: the portal page has no
        // course list to give (§WP5 / Q7).
        image:
          e.courseThumbnailUrl ||
          course?.thumbnail ||
          '',
        progress: typeof e.progress === 'number' ? e.progress : 0,
        // FIX #682: surface enrollmentId so the carousel CTA can navigate to the player.
        enrollmentId: e.id,
      }
    })
}

export function transformToCourseCards(
  enrollments: EnrollmentLike[],
  courses: CourseLike[],
  employeeEmail?: string,
  /** The workspace zone; due dates print as that zone's day. */
  timeZone?: string,
): CourseCardProp[] {
  const courseMap = new Map(courses.map((c) => [c.id, c]))
  // FIX (2026-05-14, ticket af89916d): used to cap at 6 (one page). The portal
  // preview's Trainings tab now paginates client-side via the "View More"
  // button — we need to surface every enrollment for that employee so the
  // pagination has data to reveal. Hard cap at 200 to defend against a
  // pathological 10k-row payload while still showing every realistic mailbox.
  return enrollments
    .filter((e) => !employeeEmail || e.learnerEmail === employeeEmail)
    .slice(0, 200)
    .map((e) => {
      const course = courseMap.get(e.courseId)
      const zone = e.dueTimeZone ?? timeZone
      const dueDay = dueDayOf(e.dueDate, zone)
      return {
        title: e.courseName,
        // The enrolment's own course picture first: the portal page has no
        // course list to give (§WP5 / Q7).
        image:
          e.courseThumbnailUrl ||
          course?.thumbnail ||
          '',
        // FIX (2026-05-14, ticket 9b36b6ae): same UPPER_SNAKE_CASE bug as the
        // training-slides transformer. attempts always read '0' because
        // 'COMPLETED' never matched the Title Case enum value.
        attempts: e.status === 'Completed' ? '1' : '0',
        status: e.status,
        statusColor: STATUS_COLOR_MAP[e.status] ?? STATUS_COLOR_MAP['Not Started'],
        // 2026-05-12 — surfaced for the portal-preview "Next deadline" meta.
        dueDay,
        ...(zone ? { dueTimeZone: zone } : {}),
        progress: typeof e.progress === 'number' ? e.progress : 0,
        // FIX #681: surface enrollmentId so the Trainings-tab CTA can navigate to the player.
        enrollmentId: e.id,
      }
    })
}

/**
 * The Points tab: the learner's newest ten points rows, newest first, each
 * amount with its real sign — "+50" for an award, "-20" for a deduction or a
 * revoke. Every amount used to be printed as `+${points}`, so a deduction read
 * "+-20" and the tab's "earned" filter (startsWith('+')) counted it as earned
 * (lead item E, 2026-09-26). `date` is the award's instant as the API sent it;
 * the portal words it ("5 min ago" / "قبل 5 دقائق") in the reader's language.
 *
 * `recipientId` keeps only that learner's rows. It was the learner's NAME,
 * which no row on the employee portal carries (they are the learner's own
 * ledger rows), so the real portal's history was always empty — matched by
 * recipient id, as the celebrations are (lead item A).
 */
export function transformPointsHistory(
  awards: PointsAwardEntry[],
  recipientId?: string,
): PointsHistoryProp[] {
  return awards
    .filter((a) => !recipientId || a.recipientId === recipientId)
    // A level-up is recorded as a 0-point ledger row; it is not a points
    // movement (item 1) — the roadmap and the celebration show it.
    .filter((a) => Number(a.points) !== 0)
    .sort(
      (a, b) =>
        new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime(),
    )
    .slice(0, 10)
    .map((a) => ({
      activity: a.activity,
      points: a.points > 0 ? `+${a.points}` : String(a.points),
      date: a.dateTime,
    }))
}

export function transformToBadgeCards(
  activePack: BadgePack | null,
  earnedBadgeIds: string[],
  badgeAwards: BadgeAwardEntry[] = [],
  pointsAwards: PointsAwardEntry[] = [],
  employeeEmail?: string,
): BadgeCardProp[] {
  if (!activePack) return []
  const userBadgeAwards = employeeEmail
    ? badgeAwards.filter((a) => a.employeeEmail === employeeEmail)
    : badgeAwards
  const userPointsAwards = employeeEmail
    ? pointsAwards.filter((a) => a.employeeEmail === employeeEmail)
    : pointsAwards
  const earnedKeys = new Set(earnedBadgeIds)
  // Achievement badges only (exclude level badges)
  return activePack.badges
    .filter((b) => !b.event.startsWith('level_'))
    .map((b) => {
      const award = userBadgeAwards.find((a) => a.badgeName === b.name)
      let relatedTraining: string | undefined
      let pointsEarned: number | undefined
      if (award) {
        const awardTime = new Date(award.dateTime).getTime()
        const nearby = userPointsAwards.find((p) => {
          const diff = Math.abs(new Date(p.dateTime).getTime() - awardTime)
          return diff <= 10 * 60 * 1000 && (p.trainingName || p.campaignName)
        })
        if (nearby) {
          relatedTraining = nearby.trainingName || nearby.campaignName
          pointsEarned = nearby.points
        }
      }
      // Ticket #246: the leaderboard `entries[].badges` array contains badge
      // *names* (strings), not UUIDs. Check both ID and name so the earned
      // flag is accurate regardless of which form the leaderboard returns.
      // Additionally treat a matching badgeAward row as definitive proof of
      // earning, since the activity-log is the primary source of truth.
      // A badge and its tenant copies are ONE badge — the API never awards a
      // learner both (award() dedupes the fork lineage). So a copy reads earned
      // for a learner who holds the global badge it was copied from, and the
      // reverse holds through the origins earnedBadgeKeys() adds (audit G6).
      const earned =
        earnedKeys.has(b.id) ||
        (!!b.forkedFromBadgeId && earnedKeys.has(b.forkedFromBadgeId)) ||
        earnedKeys.has(b.name) ||
        !!award;
      return {
        name: b.name,
        icon: b.icon,
        earned,
        description: b.eventDescription,
        earnedAt: award?.dateTime,
        eventCount: award?.eventCount,
        relatedTraining,
        pointsEarned,
      }
    })
}

export function transformToCertificateCards(
  enrollments: EnrollmentLike[],
  courses: CourseLike[],
  employeeEmail?: string,
): CertificateCardProp[] {
  const certCourseIds = new Set(
    courses.filter((c) => c.certificateEnabled).map((c) => c.id),
  )
  return enrollments
    .filter(
      (e) =>
        (!employeeEmail || e.learnerEmail === employeeEmail) &&
        certCourseIds.has(e.courseId),
    )
    .map((e) => ({
      // The course's name and the completion instant; the portal words both
      // ("{course} Certificate", a date or "Pending") in the reader's language.
      name: e.courseName,
      date: e.completionDate || null,
      // FIX (2026-05-14): match Title Case BE enum, not UPPER_SNAKE_CASE.
      status: e.status === 'Completed' ? 'Completed' : 'In Progress',
    }))
}

/**
 * The ids an earned-badge list holds, for transformToBadgeCards: each earned
 * badge's own id AND the global badge it is a copy of (GET /v1/portal/badges
 * /earned carries the badge's `forkedFromBadgeId`). With the copy's own origin
 * on the pack side, a badge matches across the whole fork lineage.
 */
export function earnedBadgeKeys(
  earned: ReadonlyArray<{
    badgeId: string
    badge?: { forkedFromBadgeId?: string } | null
  }>,
): string[] {
  const keys = new Set<string>()
  for (const e of earned) {
    if (e.badgeId) keys.add(e.badgeId)
    const origin = e.badge?.forkedFromBadgeId
    if (origin) keys.add(origin)
  }
  return [...keys]
}

/**
 * The points-history rows of badge awards the learner still HOLDS — what the
 * badge cards read their earned date from. A revoke leaves the award row in the
 * history (and adds a reversal keyed to the same earned badge), so a row counts
 * only while its earned badge (`sourceId`) is in the earned list, which omits
 * revoked badges. No e-mail filter: the portal endpoints return the caller's
 * rows only, and no award row carries an e-mail.
 */
export function awardsForHeldBadges<
  T extends { sourceType: string; sourceId: string },
>(rows: readonly T[], held: ReadonlyArray<{ id: string }>): T[] {
  const heldIds = new Set(held.map((e) => e.id))
  return rows.filter((r) => r.sourceType === 'BADGE' && heldIds.has(r.sourceId))
}

/**
 * The rows a celebration may announce ("You earned a badge!", "+50 points"):
 * LIVE awards only — points above zero, not revoked. A revoke leaves its award
 * row in the history, marked revoked, and adds a reversal row with negative
 * points and the same source type; neither is something the learner just
 * earned. The portal and both admin previews handed the celebration detector
 * every row, so a badge an admin had revoked — and its reversal — were
 * announced as earned (2026-09-26, audit wave 3 item 10). Pass the badge
 * celebrations only the BADGE rows of the result: the badge activity tab lists
 * challenge rows too.
 */
export function celebratableAwards<T extends { points: number; revoked?: boolean }>(
  rows: readonly T[],
): T[] {
  return rows.filter((r) => Number(r.points) > 0 && r.revoked !== true)
}

/**
 * The level-ups a celebration may announce ("Level up — Cadet!"): LEVEL rows
 * an admin has not revoked. The API records a level-up as a 'Level Up' ledger
 * row of 0 points — a level moves no points — so celebratableAwards (points
 * above zero) could never let one through (2026-09-26, item 1). A negative
 * LEVEL row would be a reversal, never a level reached.
 */
export function celebratableLevelUps<
  T extends { points: number; revoked?: boolean; sourceType?: string },
>(rows: readonly T[]): T[] {
  return rows.filter(
    (r) =>
      (r.sourceType ?? 'LEVEL') === 'LEVEL' &&
      Number(r.points) >= 0 &&
      r.revoked !== true,
  )
}

export function findActiveBadgePack(
  packs: BadgePack[],
  settings: Pick<GamificationSettings, 'activeBadgePackId'> | null,
): BadgePack | null {
  if (!packs.length) return null
  // GET /v1/portal/badge-packs marks the pack badges are awarded from — after
  // an admin customises a built-in pack that is the tenant's copy, which is
  // never marked default (audit G6). The admin endpoints mark nothing, so their
  // callers keep the selection below.
  const effective = packs.find((p) => p.isEffective === true)
  if (effective) return effective
  if (settings?.activeBadgePackId) {
    const found = packs.find((p) => p.id === settings.activeBadgePackId)
    if (found) return found
  }
  // Fallback: first default or first pack
  return packs.find((p) => p.isDefault) ?? packs[0]
}

/** A level as GET /v1/portal/levels returns it (LevelResponseDto). */
interface LevelLike {
  id: string
  name: string
  pointsRequired: number
  order?: number
  imageUrl?: string
}

/**
 * Level art the roadmap can render as an <img>: a path or a URL. The global
 * seed stores a Lucide icon NAME ("shield") in imageUrl, which would render as
 * a broken image, so anything else falls back to the numbered medallion.
 */
function levelImage(imageUrl?: string): string {
  const v = imageUrl?.trim() ?? ''
  return v.startsWith('/') ||
    v.startsWith('http://') ||
    v.startsWith('https://') ||
    v.startsWith('data:')
    ? v
    : ''
}

/**
 * The portal roadmap and the learner's place on it, from the portal API:
 *   levels       GET /v1/portal/levels         the workspace's level ladder
 *   earnedLevel  GET /v1/portal/levels/earned  the level the API says they hold
 *   totalPoints  GET /v1/portal/points/summary the balance it was picked from
 * Rungs are numbered 1..n in ladder order (sortLadder — the API's order, so a
 * tied threshold breaks the same way here as in the earned level; audit T13)
 * — PortalPreview compares `currentLevel` with those numbers — and the current
 * level is the earned level's rung, 0 before the first. The portal, the two
 * admin view-as previews and the Customise Portal builder all build their
 * roadmap here; transformLevels(), which read the ladder off "level_*" badges
 * no badge pack has (always empty, "Level 0" — audit G9), is gone (item 1).
 */
export function buildLevelProgress(input: {
  levels: ReadonlyArray<LevelLike>
  earnedLevel: { id: string } | null
  totalPoints: number
  achievedBadgeIds: string[]
}): {
  levelData: LevelDataProp[]
  userProgress: {
    currentLevel: number
    currentPoints: number
    achievedBadgeIds: string[]
  }
} {
  const ladder = sortLadder(input.levels)
  const earnedId = input.earnedLevel?.id
  const currentLevel = earnedId
    ? ladder.findIndex((l) => l.id === earnedId) + 1
    : 0
  return {
    levelData: ladder.map((l, i) => ({
      id: i + 1,
      name: l.name,
      pointsRequired: l.pointsRequired,
      badge: levelImage(l.imageUrl),
    })),
    userProgress: {
      currentLevel,
      currentPoints: input.totalPoints,
      achievedBadgeIds: input.achievedBadgeIds,
    },
  }
}

/**
 * The learner's earned badges as ids of the pack the portal shows — a badge
 * held under the global original is the copy on display, one badge to the API —
 * or their own id when that pack does not show them. One id per badge: the
 * roadmap counts them and checks the pack's badges against them.
 */
export function achievedBadgeIdsIn(
  activePack: BadgePack | null,
  earned: ReadonlyArray<{
    badgeId: string
    badge?: { forkedFromBadgeId?: string } | null
  }>,
): string[] {
  const ids = new Set<string>()
  for (const e of earned) {
    const keys = [e.badgeId, e.badge?.forkedFromBadgeId].filter(
      (k): k is string => !!k,
    )
    const shown = activePack?.badges.find(
      (b) =>
        keys.includes(b.id) ||
        (!!b.forkedFromBadgeId && keys.includes(b.forkedFromBadgeId)),
    )
    ids.add(shown?.id ?? e.badgeId)
  }
  return [...ids]
}
